<template>

    <th :style="style" :data-weight="weight">

        <span v-if="isSortable" class="sortable" @click="onSortClick" :class="{ 'sort-active': isSortActive }">
            <slot></slot>
            <i v-if="isSortActive" :class="sortClass"></i>
        </span>

        <span v-else>
            <slot></slot>
        </span>

    </th>

</template>

<script lang="ts">

import { Component, Prop, Vue } from 'vue-facing-decorator';
import { sum } from 'lodash';

import type { ComponentProps } from '@wittignl/js-core';
import { createComponentMixin } from '@wittignl/js-core';

export interface TableHeaderColumnValue {
    direction: 'ASC' | 'DESC'
    field: string
}

@Component({
    name: 'ui-table-header-column',
    emits: ['update:modelValue']
})
export default class TableHeaderColumn extends Vue {

    @Prop({ type: Boolean })
    sortable: boolean;

    @Prop({ type: Object })
    modelValue: TableHeaderColumnValue;

    @Prop({ type: String })
    field: string;

    @Prop({ type: [Number, String] })
    weight: number | string;

    totalWeight: number = null;

    mounted() {

        this.$nextTick(() => this.updateTotalWeight());
    }

    updated() {

        this.updateTotalWeight();
    }

    get isSortable() {

        // Is sortable when value prop is provided
        if(this.modelValue !== undefined){
            return true;
        }

        return this.sortable;
    }

    get isSortActive() {

        return this.modelValue && this.modelValue.field === this.field;
    }

    get sortClass() {

        if(!this.modelValue){
            return null;
        }

        if(this.modelValue.direction === 'ASC'){
            return 'bx bx-chevron-up';
        }
        else if(this.modelValue.direction === 'DESC'){
            return 'bx bx-chevron-down';
        }

        return null;
    }

    get style() {

        if(!this.weight || !this.totalWeight){
            return null;
        }

        const weight = parseFloat(this.weight as string);
        const width = Math.round((weight / this.totalWeight) * 100);

        return { width: width + '%' };
    }

    updateTotalWeight() {

        if(!this.weight){
            return;
        }

        const $parentEl = this.$parent.$el;
        const $siblingColumns = [...$parentEl.querySelectorAll('th[data-weight]')];
        const siblingWeights = $siblingColumns.map($column => parseFloat($column.dataset.weight));

        this.totalWeight = sum(siblingWeights);
    }

    onSortClick() {

        let newDirection = this.modelValue.direction;

        if(this.modelValue && this.modelValue.field === this.field){
            newDirection = this.modelValue.direction === 'ASC' ? 'DESC' : 'ASC';
        }

        this.$emit('update:modelValue', {
            field: this.field,
            direction: newDirection
        });
    }
}

export type TableHeaderColumnComp = InstanceType<typeof TableHeaderColumn>
export type TableHeaderColumnProps = ComponentProps<TableHeaderColumnComp>
export const tableHeaderColumnCompMixin = createComponentMixin(TableHeaderColumn)

</script>